import Base from '@/resources/Base'
import { emailRe } from '@/utilities/regex/email'
import { validatePassword } from '@/utilities/user/validation'
import { PasswordScoreColorEnum } from '@/constants/password-score.constants'
import { getPasswordStrengthColor } from '@/utilities/passwordstrength.helper'

const userFieldValidation = {
  firstname: [
    {
      required: true,
      message: "Please provide the user's first name.",
      trigger: 'blur'
    }
  ],
  lastname: [
    {
      required: true,
      message: "Please provide the user's last name.",
      trigger: 'blur'
    }
  ],
  description: [
    {
      max: 256,
      message: 'Max length is 256 characters.',
      trigger: 'blur'
    }
  ],
  email: [
    {
      required: true,
      trigger: 'blur'
    }
  ],
  password: [
    {
      required: true,
      min: 8,
      max: 64,
      trigger: 'change'
    }
  ],
  confirmPassword: [
    {
      required: true,
      trigger: 'blur'
    }
  ],
  roles: [
    {
      required: true,
      message: 'Please select 1 or more user role.',
      trigger: 'blur'
    }
  ]
}

export default class User extends Base {
  constructor (uuid = null, attributes = {}, options = {}) {
    super(uuid, attributes, options)
    this.roleNames = _.map(this.roles, 'name')

    this.notificationEmailPreferences = {
      trail: {},
      host: {}
    }

    this.notificationSlackPreferences = {
      trail: {},
      host: {}
    }
  }

  /**
    Email notification preferences for the User.
    Default settings below:
    {
      host_high_score_increment: 100
      host_risk_change: false
      host_status_change: false
      subscribed: true
      trail_assignment_change: true
      trail_high_score_increment: 100
      trail_progression: true
      trail_risk_change: true
      trail_status_change: true
    }
  */
  setEmailNotificationPreferences (preferences) {
    for (let [prefKey, prefValue] of Object.entries(preferences)) {
      if (prefKey.startsWith('trail')) {
        this.notificationEmailPreferences.trail[prefKey] = prefValue
      } else if (prefKey.startsWith('host')) {
        this.notificationEmailPreferences.host[prefKey] = prefValue
      } else {
        this.notificationEmailPreferences[prefKey] = prefValue
      }
    }
    if (!_.isEmpty(preferences.secondary_emails)) {
      preferences.secondary_emails.forEach(ccEmail => {
        ccEmail.edit = false
      })
    }
  }

  getEmailNotificationPreferencesText (prefKey) {
    switch (prefKey) {
      case 'subscribed':
        return 'Get email updates for issues when'
      case 'host_risk_change':
        return 'Risk Level Change'
      case 'host_status_change':
        return 'Agent goes offline'
      case 'trail_assignment_change':
        return 'Assignee Change on Existing Threat Sequence'
      case 'trail_progression':
        return 'New Threat Progression'
      case 'trail_risk_change':
        return 'Risk Level Change'
      case 'trail_status_change':
        return 'Workflow Status Change'
      default:
        return ''
    }
  }

  /**
    Slack notification preferences for the User.
    Default settings below:
    {
      host_high_score_increment: 100
      host_risk_change: false
      host_status_change: false
      subscribed: true
      trail_assignment_change: true
      trail_high_score_increment: 100
      trail_progression: true
      trail_risk_change: true
      trail_status_change: true
    }
  */
  setSlackNotificationPreferences (preferences) {
    for (let [prefKey, prefValue] of Object.entries(preferences)) {
      if (prefKey.startsWith('trail')) {
        this.notificationSlackPreferences.trail[prefKey] = prefValue
      } else if (prefKey.startsWith('host')) {
        this.notificationSlackPreferences.host[prefKey] = prefValue
      } else {
        this.notificationSlackPreferences[prefKey] = prefValue
      }
    }
  }

  getSlackNotificationPreferencesText (prefKey) {
    switch (prefKey) {
      case 'subscribed':
        return 'Receive alerts and notifications in Slack channels :'
      case 'host_risk_change':
        return 'Risk Level Change'
      case 'host_status_change':
        return 'Agent goes offline'
      case 'trail_assignment_change':
        return 'Assignee Change on Existing Threat Sequence'
      case 'trail_progression':
        return 'New Threat Progression'
      case 'trail_risk_change':
        return 'Risk Level Change'
      case 'trail_status_change':
        return 'Workflow Status Change'
      default:
        return ''
    }
  }

  resetFields () {
    if (!this.id) {
      Object.assign(this, {
        firstname: '',
        lastname: '',
        description: '',
        email: '',
        password: '',
        confirmPassword: '',
        passwordStrengthScore: 0,
        passwordStrengthColor: PasswordScoreColorEnum[0],
        active: true,
        roles: []
      })
    }
  }

  getFormRef () {
    return this.formRef
  }

  setFormRef (ref) {
    this.formRef = ref
  }

  validateEmail (rule, value, callback) {
    if (value === '') {
      callback(new Error("Please input the user's email address"))
    } else {
      emailRe.test(value) ? callback() : callback(new Error('Invalid email format'))
    }
  }

  validatePassword (rule, value, callback) {
    let validPassword = validatePassword(value)
    let passwordStrengthMeta = getPasswordStrengthColor(validPassword.score)
    this.passwordStrengthScore = passwordStrengthMeta.percentage
    this.passwordStrengthColor = passwordStrengthMeta.color
    if (validPassword.score > 2) {
      if (this.confirmPassword !== '') {
        this.formRef.validateField('confirmPassword')
      }
      callback()
    } else {
      callback(validPassword.error)
    }
  }

  validateConfirmPassword (rule, value, callback) {
    if (value === '') {
      callback(new Error('Please input the password again'))
    } else if (value !== this.password) {
      callback(new Error('Passwords did not match!'))
    } else {
      callback()
    }
  }

  setValidationRules () {
    this.validationRules = userFieldValidation
    this.validationRules.email[0].validator = this.validateEmail.bind(this)
    this.validationRules.password[0].validator = this.validatePassword.bind(this)
    this.validationRules.confirmPassword[0].validator = this.validateConfirmPassword.bind(this)
  }

  resetValidation () {
    if (this.formRef) {
      this.formRef.clearValidate()
      this.formRef.resetFields()
    }
  }
}



// WEBPACK FOOTER //
// ./src/resources/models/User.js